/**
 * /api/views, from the browser: short links for views too big to carry inline.
 *
 * A saved view is { id, state, created_by, created_at } in Azure SQL
 * (sql/cpc_views_001.sql). The state is a SharedView — people as opaque keys, never
 * names (state/sharedView.ts) — so the row holds nothing a reader outside the app can
 * use, and a reader inside it is still cut to their own access by their own bundle.
 */
import { apiFetch, TOKEN_HEADER } from '@/auth/apiFetch'
import { withViewAs } from '@/data/viewAs'
import { parseSharedView, type SharedView } from './sharedView'

const VIEWS = '/api/views'

const headers = (token: string | null, body: boolean) => {
  // The view-as header too: the API refuses to SAVE while viewing as someone.
  const h = withViewAs({ Accept: 'application/json' })
  if (token) h[TOKEN_HEADER] = token
  if (body) h['Content-Type'] = 'application/json'
  return h
}

/** Saves the view; resolves to its id. Throws when saved links are unavailable. */
export async function saveView(token: string | null, view: SharedView): Promise<string> {
  const res = await apiFetch(VIEWS, { method: 'POST', headers: headers(token, true), body: JSON.stringify({ state: view }) })
  if (!res.ok) throw new Error(`Saving the view failed: ${res.status}`)
  const { id } = (await res.json()) as { id?: unknown }
  if (typeof id !== 'string') throw new Error('Saving the view failed')
  return id
}

/** A saved view, or null when there is no such view (or it cannot be read). */
export async function loadView(token: string | null, id: string): Promise<SharedView | null> {
  const res = await apiFetch(`${VIEWS}/${encodeURIComponent(id)}`, { headers: headers(token, false) })
  if (!res.ok) return null
  const body = (await res.json().catch(() => null)) as { state?: unknown } | null
  return parseSharedView(body?.state)
}
